import React from 'react';
import Button from '../../components/common/Button';
import '../../styles/landing-page.css';

const LandingPage: React.FC = () => {
  return (
    <div className="landing-page">
      {/* Hero - split layout */}
      <section className="hero">
        <div className="container hero__inner">
          <div className="hero__text fade-in-up">
            <h1 className="hero__headline">
              Daily Activity Reports for Security Teams
            </h1>
            <p className="hero__sub">
              Track guard activity, monitor sites in real time, and generate
              daily reports, all from one simple platform.
            </p>
            <div className="hero__buttons fade-in-up-delay">
              <Button variant="primary" size="lg" href="/pricing">
                Get Started
              </Button>
              <Button variant="outline" size="lg" href="/features">
                View Features
              </Button>
            </div>
          </div>

          <div className="hero__phones fade-in-up-delay-2">
            <img src="/app-generate-report.png"  alt="Report generation"     className="hero__phone hero__phone--back" />
            <img src="/app-dashboard.png"       alt="Dashboard overview"    className="hero__phone hero__phone--mid" />
            <img src="/app-monitoring.png"       alt="Live map monitoring"   className="hero__phone hero__phone--front" />
          </div>
        </div>
      </section>

      {/* Quick Setup - video showcase */}
      <section className="setup section">
        <div className="container">
          <div className="setup__inner fade-in-up">
            <div className="setup__video-wrap">
              <video
                className="setup__video"
                src="/app-setup-demo.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
            <div className="setup__text">
              <h2 className="setup__heading">Quick &amp; Easy Setup</h2>
              <p className="setup__desc">
                Get your team up and running in minutes, not weeks. No complex
                onboarding or training required. Just sign up, add your sites,
                invite your guards, and start receiving daily activity reports.
              </p>
              <Button variant="primary" size="lg" href="/pricing">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features - 5 cards */}
      <section className="value-props section">
        <div className="container">
          <div className="text-center fade-in-up">
            <h2 className="section-heading">Why Choose Inferium Reports?</h2>
            <p className="section-subtext">
              Professional-grade reporting software with AI-powered features, at a price that works for any operation.
            </p>
          </div>

          <div className="cards fade-in-up-delay">
            <div className="card">
              <div className="card__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>AI-Driven Summarizations</h3>
              <p>
                Automatically generate concise summaries from guard reports using AI. Turn hours of reading into clear, actionable highlights.
              </p>
            </div>

            <div className="card">
              <div className="card__icon card__icon--alt">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 5H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M9 3V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 5C3 5 5 14 12 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M15 5C15 5 13 14 12 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 14V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M14 19L21 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M21 15V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 15L21 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 19H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Language Translations</h3>
              <p>
                Guards submit reports in their preferred language and clients receive them fully translated. No language barriers.
              </p>
            </div>

            <div className="card">
              <div className="card__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6A2 2 0 0 0 4 4V20A2 2 0 0 0 6 22H18A2 2 0 0 0 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Professional-Grade Reports</h3>
              <p>
                Polished, client-ready daily activity reports generated in seconds. The kind of output that wins contracts.
              </p>
            </div>

            <div className="card">
              <div className="card__icon card__icon--alt">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Real-time Monitoring</h3>
              <p>
                Live map tracking, guard status updates, and instant incident alerts across every site you manage.
              </p>
            </div>

            <div className="card">
              <div className="card__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <line x1="12" y1="1" x2="12" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Affordable Pricing</h3>
              <p>
                Enterprise features without the enterprise price tag. Built for security companies of every size.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta section">
        <div className="container">
          <div className="cta__inner text-center fade-in-up">
            <h2 className="section-heading">
              Ready to Simplify Your Daily Activity Reports?
            </h2>
            <p className="section-subtext section-subtext--narrow">
              Contact our team for a custom demonstration and see how Inferium
              Reports delivers daily activity reports your clients will love.
            </p>
            <div className="cta__buttons fade-in-up-delay">
              <Button variant="primary" size="lg" href="/pricing">
                Get Started
              </Button>
              <Button variant="secondary" size="lg" href="/contact">
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
